import { ContentCopy, Download, OpenInNew, Print, QrCode2Outlined, Refresh } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Grid, IconButton, Link, Tooltip, Typography } from "@mui/material";
import QRCode from "qrcode.react";
import { useEffect, useRef, useState } from "react";
import { SecaoConfiguracao } from "../../../../../core/components/SecaoConfiguracao";
import { ConfirmacaoAcaoModal } from "../../../../../core/components/ConfirmacaoAcaoModal";
import { useSnackbar } from "../../../../../core/contexts/SnackbarContext";
import { useMesasQRCode } from "../../../../../pedidos/hooks/useMesasQRCode";
import { IQRCodeMesa } from "../../../../../pedidos/interface/ISessaoMesa";
import { useMarketplaces } from "../../../../hooks/useMarketplaces";
import { IMarketplace } from "../../../../interfaces/IMarketplace";
import { urlMesa } from "../../../../utils/urlBaseCardapio";

interface QRCodesMesasPaperProps {
    marketplace: IMarketplace;
    refresh: () => void;
}

const TAMANHO_QRCODE = 160;

/** Um QR Code por mesa (1..número de mesas configurado), com download e folha para impressão. */
export const QRCodesMesasPaper = ({ marketplace, refresh }: QRCodesMesasPaperProps) => {
    const { gerarQRCodes, loading } = useMesasQRCode();
    const { salvar, loading: salvando } = useMarketplaces();
    const snackbar = useSnackbar();
    const [qrcodes, setQRCodes] = useState<IQRCodeMesa[]>([]);
    const [confirmarRegeracao, setConfirmarRegeracao] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (marketplace?.uuid) {
            gerarQRCodes(marketplace.uuid).then(setQRCodes);
        }
    }, [marketplace?.uuid, marketplace?.configuracao?.mesa?.versaoToken]);

    const imagemMesa = (mesaId: number) => {
        const canvas = containerRef.current?.querySelector<HTMLCanvasElement>(`canvas[data-mesa="${mesaId}"]`);
        return canvas?.toDataURL("image/png");
    };

    const baixar = (mesaId: number) => {
        const imagem = imagemMesa(mesaId);
        if (!imagem) return;
        const link = document.createElement("a");
        link.href = imagem;
        link.download = `${marketplace.configuracao?.slug ?? "mesa"}-mesa-${mesaId}.png`;
        link.click();
    };

    // O link fica visível para testar a mesa no computador, sem precisar ler o QR com o celular.
    const copiarLink = (link: string) => {
        navigator.clipboard.writeText(link)
            .then(() => snackbar("Link da mesa copiado", { severity: "info" }))
            .catch(() => snackbar("Não foi possível copiar o link", { severity: "error" }));
    };

    const imprimir = () => {
        const janela = window.open("", "_blank");
        if (!janela) {
            return snackbar("Permita pop-ups para imprimir os QR Codes", { severity: "warning" });
        }

        const nome = marketplace.configuracao?.nomePublico ?? marketplace.nome ?? "";
        const cartoes = qrcodes.map(({ mesaId }) => `
            <div class="cartao">
                <div class="nome">${nome}</div>
                <img src="${imagemMesa(mesaId)}" />
                <div class="mesa">Mesa ${mesaId}</div>
                <div class="dica">Aponte a câmera para fazer seu pedido</div>
            </div>`).join("");

        janela.document.write(`
            <html><head><title>QR Codes das mesas</title>
            <style>
                body { font-family: sans-serif; margin: 0; }
                .grade { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; padding: 12px; }
                .cartao { border: 1px dashed #999; border-radius: 8px; padding: 12px; text-align: center; page-break-inside: avoid; }
                .cartao img { width: 160px; height: 160px; }
                .nome { font-weight: 600; margin-bottom: 8px; }
                .mesa { font-size: 22px; font-weight: 700; margin-top: 8px; }
                .dica { font-size: 12px; color: #555; margin-top: 4px; }
            </style></head>
            <body><div class="grade">${cartoes}</div></body></html>`);
        janela.document.close();
        janela.onload = () => janela.print();
    };

    const regerar = async () => {
        const versaoAtual = marketplace.configuracao?.mesa?.versaoToken ?? 1;
        const salvo = await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                mesa: { ...marketplace.configuracao?.mesa, versaoToken: versaoAtual + 1 },
            },
        });

        setConfirmarRegeracao(false);
        if (salvo) {
            snackbar("Novos QR Codes gerados. Imprima e substitua os das mesas.", { severity: "success" });
            refresh();
        }
    };

    return (
        <SecaoConfiguracao
            titulo="QR Codes das mesas"
            descricao="Cada mesa tem o seu QR Code. Quem ler entra na conta daquela mesa."
            icone={<QrCode2Outlined />}
            acoes={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="Invalida os QR Codes impressos e gera novos">
                        <span>
                            <Button size="small" variant="outlined" color="warning" startIcon={<Refresh />} onClick={() => setConfirmarRegeracao(true)} disabled={!qrcodes.length}>
                                Gerar novos
                            </Button>
                        </span>
                    </Tooltip>
                    <Button size="small" variant="contained" startIcon={<Print />} onClick={imprimir} disabled={!qrcodes.length}>
                        Imprimir todos
                    </Button>
                </Box>
            }
        >
            <ConfirmacaoAcaoModal
                open={confirmarRegeracao}
                titulo="Gerar novos QR Codes?"
                descricao="Os QR Codes já impressos param de funcionar imediatamente. Use quando um QR Code tiver sido copiado ou fotografado indevidamente."
                onCancelar={() => setConfirmarRegeracao(false)}
                onConfirmar={regerar}
                loading={salvando}
            />
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : !qrcodes.length ? (
                <Alert severity="info">
                    Nenhuma mesa configurada. Defina o número de mesas em{" "}
                    <Link href="/configuracoes/numero-mesas">Configurações › Número de mesas</Link> para gerar os QR Codes.
                </Alert>
            ) : (
                <Grid container spacing={2} ref={containerRef}>
                    {qrcodes.map(({ mesaId, token }) => (
                        <Grid item xs={6} sm={4} md={3} key={mesaId}>
                            <Box sx={{
                                p: 1.5,
                                borderRadius: "12px",
                                border: theme => `1px solid ${theme.palette.divider}`,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1,
                                background: "#FFF",
                            }}>
                                <QRCode value={urlMesa(marketplace, token)} size={TAMANHO_QRCODE} data-mesa={mesaId} includeMargin />
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography fontWeight={700}>Mesa {mesaId}</Typography>
                                    <Tooltip title="Baixar PNG">
                                        <IconButton size="small" onClick={() => baixar(mesaId)}>
                                            <Download fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%", minWidth: 0 }}>
                                    <Tooltip title={urlMesa(marketplace, token)}>
                                        <Link
                                            href={urlMesa(marketplace, token)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="caption"
                                            sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 0.5 }}
                                        >
                                            <OpenInNew sx={{ fontSize: 14, flexShrink: 0 }} />
                                            {urlMesa(marketplace, token).replace(/^https?:\/\//, "")}
                                        </Link>
                                    </Tooltip>
                                    <Tooltip title="Copiar link">
                                        <IconButton size="small" onClick={() => copiarLink(urlMesa(marketplace, token))}>
                                            <ContentCopy sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            )}
        </SecaoConfiguracao>
    );
};
